import {
  Button,
  FlexBox,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { isFileUIPart, isToolUIPart, type ChatStatus } from 'ai';
import classnames from 'classnames';

import { type Starter } from 'utils/sidekickStarters';

import { useChatScroll } from '../../../../hooks/useChatScroll';
import { type MimUIMessage } from '../../../../utils/sdk/structuredResponse';
import { StatusRow } from '../../../statusRow/statusRow';
import { SidekickToolPartIndicator } from '../sidekickToolPartIndicator/sidekickToolPartIndicator';

import { SidekickAttachmentTile } from './components/sidekickAttachmentTile/sidekickAttachmentTile';
import { SidekickChoices } from './components/sidekickChoices/sidekickChoices';
import { SidekickMessage } from './components/sidekickMessage/sidekickMessage';
import { SidekickPlan } from './components/sidekickPlan/sidekickPlan';
import { SidekickReasoning } from './components/sidekickReasoning/sidekickReasoning';
import { WorkingIndicator } from './components/workingIndicator/workingIndicator';
import styles from './sidekickMessageList.module.scss';

type SidekickMessageListProps = {
  messages: MimUIMessage[];
  starters: readonly Starter[];
  status: ChatStatus;
  isLoadingOlder?: boolean;
  onSelectChoice: (text: string) => void;
  onRetry: () => void;
  onStartPlan: () => void;
  onReachTop?: () => void;
};

const IN_FLIGHT_TOOL_STATES = ['input-streaming', 'input-available'];

const isBetweenSteps = (message: MimUIMessage): boolean =>
  !message.parts.some((part) => {
    if (isToolUIPart(part)) return IN_FLIGHT_TOOL_STATES.includes(part.state);
    if (part.type === 'reasoning') return part.state === 'streaming';
    return part.type === 'data-message';
  });

export const SidekickMessageList = ({
  messages,
  starters,
  status,
  isLoadingOlder = false,
  onSelectChoice,
  onRetry,
  onStartPlan,
  onReachTop,
}: SidekickMessageListProps): JSX.Element => {
  const { containerRef, isScrolledUp, scrollToLatest } = useChatScroll(
    messages,
    { onReachTop, isLoadingOlder },
  );

  if (messages.length === 0) {
    if (starters.length === 0) {
      return (
        <div className={styles.messages}>
          <FlexBox alignItems="center" justifyContent="center" height="100%">
            <Text.Paragraph subdued>Ask me about Dixa.</Text.Paragraph>
          </FlexBox>
        </div>
      );
    }

    return (
      <div className={styles.messages}>
        <FlexBox flexDirection="column" gap="200">
          <Text.Paragraph subdued>What would you like to do?</Text.Paragraph>
          <SidekickChoices
            choices={starters.map(({ label }) => label)}
            onSelect={onSelectChoice}
          />
        </FlexBox>
      </div>
    );
  }

  const lastMessage = messages[messages.length - 1];
  const isWorking = status === 'submitted' || status === 'streaming';
  const showWorkingIndicator = isWorking && isBetweenSteps(lastMessage);
  const isIncomplete =
    (status === 'ready' || status === 'error') &&
    (lastMessage.role === 'user' ||
      lastMessage.parts.some((part) => part.type === 'data-interrupted'));

  return (
    <div className={styles.root}>
      <div ref={containerRef} className={styles.messages}>
        <FlexBox flexDirection="column" gap="200">
          {isLoadingOlder && (
            <FlexBox justifyContent="center" padding="200 0">
              <Spinner size={16} />
            </FlexBox>
          )}
          {messages.map((message, messageIndex) => {
            const isLastMessage = messageIndex === messages.length - 1;
            const attachments = message.parts.filter(isFileUIPart);

            const pageEvent = message.parts.find(
              (part) => part.type === 'data-page-event',
            );
            if (pageEvent) {
              return (
                <StatusRow
                  key={message.id}
                  icon={<Icon name="info" color="secondary" />}
                >
                  {pageEvent.data}
                </StatusRow>
              );
            }

            return (
              <FlexBox key={message.id} flexDirection="column" gap="100">
                {attachments.length > 0 && (
                  <FlexBox
                    flexWrap="wrap"
                    gap="200"
                    justifyContent={
                      message.role === 'user' ? 'flex-end' : 'flex-start'
                    }
                  >
                    {attachments.map((attachment, index) => (
                      <SidekickAttachmentTile
                        key={`${message.id}-file-${index}`}
                        attachment={attachment}
                      />
                    ))}
                  </FlexBox>
                )}
                {message.parts.map((part, index) => {
                  if (part.type === 'data-message') {
                    return (
                      <SidekickMessage
                        key={`${message.id}-${index}`}
                        role={message.role}
                        text={part.data}
                      />
                    );
                  }
                  if (part.type === 'reasoning') {
                    return (
                      <SidekickReasoning
                        key={`${message.id}-${index}`}
                        text={part.text}
                        state={part.state}
                      />
                    );
                  }
                  if (part.type === 'text') {
                    // Assistant text is the structured output
                    if (message.role === 'assistant') return null;
                    return (
                      <SidekickMessage
                        key={`${message.id}-${index}`}
                        role={message.role}
                        text={part.text}
                      />
                    );
                  }
                  if (
                    part.type === 'data-subagent-open' ||
                    part.type === 'data-subagent-close'
                  ) {
                    return null;
                  }
                  if (part.type === 'data-choices') {
                    if (!isLastMessage) return null;
                    /** Anthropic SDK does not support minItems/maxItems,
                     *  we need to decide here whether to render strucuted output or not.
                     */
                    const choices =
                      part.data.length >= 2 ? part.data.slice(0, 8) : [];
                    return (
                      <SidekickChoices
                        key={`${message.id}-${index}`}
                        choices={choices}
                        onSelect={onSelectChoice}
                      />
                    );
                  }
                  if (part.type === 'data-plan') {
                    return (
                      <SidekickPlan
                        key={`${message.id}-${index}`}
                        plan={part.data}
                        isLatest={isLastMessage}
                        onStart={isWorking ? undefined : onStartPlan}
                      />
                    );
                  }
                  if (isToolUIPart(part)) {
                    return (
                      <SidekickToolPartIndicator
                        key={`${message.id}-${index}`}
                        part={part}
                      />
                    );
                  }
                  return null;
                })}
              </FlexBox>
            );
          })}
          {isIncomplete && (
            <FlexBox alignItems="center" gap="200">
              <Text.Paragraph variant="4" subdued>
                This response didn&apos;t finish.
              </Text.Paragraph>
              <Button variant="secondary" onClick={onRetry}>
                Retry
              </Button>
            </FlexBox>
          )}
          {showWorkingIndicator && <WorkingIndicator />}
        </FlexBox>
      </div>
      <div
        className={classnames(styles.jumpToLatest, {
          [styles.visible]: isScrolledUp,
        })}
        aria-hidden={!isScrolledUp}
      >
        <Tooltip content="Jump to latest">
          <IconButton
            icon="arrow-down"
            variant="secondary"
            aria-label="Jump to latest"
            tabIndex={isScrolledUp ? undefined : -1}
            onClick={scrollToLatest}
          />
        </Tooltip>
      </div>
    </div>
  );
};
